<template>
  <!-- eslint-disable max-len -->
  <BaseFetchData :action="fetchEmailMessage">
    <template #default="fetchDataProps">
      <div v-if="fetchDataProps.status.resolved" class="flex-1 flex flex-col">
        <div class="flex-none flex gap-4 justify-end px-4 py-2">
          <div class="flex-none flex space-x-1 rounded-lg bg-gray-100 p-0.5">
            <portal to="write-email-message-dialog-top-right-corner">
              <BaseButton primary outline @click="handleGoBack"> Back </BaseButton>
            </portal>
            <button
              class="flex items-center rounded-md py-2 pl-2 pr-2 text-sm font-semibold lg:pr-3"
              :class="[mode === 'edit' ? 'bg-white shadow' : '']"
              role="tab"
              type="button"
              tabindex="-1"
              @click="mode = 'edit'"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                :class="[mode === 'edit' ? 'text-blue-600' : 'text-gray-600']"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                /></svg
              ><span class="sr-only lg:not-sr-only lg:ml-2 text-slate-600">Edit</span>
            </button>
            <button
              class="flex items-center rounded-md py-2 pl-2 pr-2 text-sm font-semibold lg:pr-3"
              :class="[mode === 'preview' ? 'bg-white shadow' : '']"
              role="tab"
              type="button"
              tabindex="0"
              @click="mode = 'preview'"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                :class="[mode === 'preview' ? 'text-blue-600' : 'text-gray-600']"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                /></svg
              ><span class="sr-only lg:not-sr-only lg:ml-2 text-slate-900">Preview</span>
            </button>
          </div>
          <button
            type="button"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white disabled:text-gray-600 bg-green-600 disabled:bg-gray-200 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            :disabled="updating || !wip"
            @click="updateEmailMessage"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 -ml-0.5 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" /></svg
            ><span class="sr-only lg:not-sr-only">Update</span>
          </button>
        </div>
        <div class="flex-1">
          <GrapesJsEmailEditor
            v-show="mode === 'edit'"
            :project-data="emailMessage.builder_data || defaultBuilderData"
            :on-load-assets="fetchAssets"
            :on-store-assets="storeAssets"
            :on-remove-asset="deleteAsset"
            v-model="wip"
          />

          <iframe
            v-show="mode === 'preview'"
            src="about:blank"
            :srcdoc="emailPreview"
            class="w-full h-full"
          />
        </div>
      </div>
    </template>
  </BaseFetchData>
</template>

<script>
import { emailMessagesClient, emailMessageAssetsClient } from '@/api/clients';
import components from '@/components/GrapesJsEmailEditor';

export default {
  components,
  props: {
    emailMessageId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      emailMessage: null,
      assets: [],
      defaultBuilderData: {
        pages: [
          {
            component: `
              <mjml>
                <mj-body>
                  <mj-section>
                    <mj-column>
                      <mj-text>Insert text here</mj-text>
                    </mj-column>
                  </mj-section>
                </mj-body>
              </mjml>
            `,
          },
        ],
      },
      wip: null,
      mode: 'edit',
      updating: false,
    };
  },
  computed: {
    emailPreview() {
      if (this.wip) {
        return this.wip.mjml.html;
      }

      if (this.emailMessage) {
        return this.emailMessage.content_html;
      }

      return '';
    },
  },
  methods: {
    async fetchEmailMessage() {
      try {
        const response = await emailMessagesClient.read(this.emailMessageId);

        this.emailMessage = response.data;
      } catch (error) {
        //
      }
    },
    fetchAssets() {
      return emailMessageAssetsClient.list(this.emailMessageId);
    },
    storeAssets(data) {
      return emailMessageAssetsClient.store(this.emailMessageId, data).then(() => {
        this.fetchAssets();
      });
    },
    deleteAsset(mediaId) {
      return emailMessageAssetsClient.destroy(this.emailMessageId, mediaId).then(() => {
        this.fetchAssets();
      });
    },
    async updateEmailMessage() {
      if (!this.wip) {
        return;
      }

      this.updating = true;

      try {
        const { data, mjml } = this.wip;
        const response = await emailMessagesClient.update(this.emailMessageId, {
          content_html: mjml.html,
          builder_data: { ...data, assets: [] },
        });

        this.emailMessage = response.data;
        this.wip = null;
        this.$toastr.success('Email message updated!');
      } catch (error) {
        this.$toastr.error(error.message || 'Email message update failed!');
      } finally {
        this.updating = false;
      }
    },
    handleGoBack() {
      if (!this.wip) {
        this.$emit('edit');
        return;
      }

      this.$modal.show({
        component: () => import('./UnsavedChangesNotify.vue'),
        on: {
          confirm: () => {
            this.updateEmailMessage().then(() => {
              this.$emit('edit');
            });
          },
          deny: () => {
            this.$emit('edit');
          },
        },
        hideEvent: 'close',
      });
    },
  },
};
</script>
